import { ApiProperty } from '@nestjs/swagger';
import { JobPendingResourceInterface } from '../interfaces/job-pending-resource.interface';
import { Exclude, Expose, Type } from 'class-transformer';
import { ContractorPaymentTypeEnum } from '../../contractor/enums/contractor-payment-type.enum';
import { PhaseStructureTypeEnum } from '../../phase/enums/phase-structure-type.enum';
import { IsEnum, IsInt, IsOptional, Min } from 'class-validator';
import { PhaseTypeEnum } from '../../phase/enums/phase-type.enum';

@Exclude()
export class JobPendingResourceDto implements JobPendingResourceInterface {
  //
  // Job fields
  //
  @ApiProperty({
    title: 'Job ID (synonym for React Admin only)',
  })
  @Expose()
  id!: string;

  @ApiProperty({
    title: 'Job ID',
  })
  @Expose()
  jobId!: string;

  @ApiProperty({
    title: 'Job Lot',
  })
  @Expose()
  jobLot!: string;

  @ApiProperty({
    title: 'Job Block',
  })
  @Expose()
  jobBlock!: string;

  @ApiProperty({
    title: 'Job Phase ID',
  })
  @Expose()
  jobPhaseId!: string;

  @ApiProperty({
    type: 'date-time',
    title: 'Created At',
  })
  @Expose()
  @Type(() => Date)
  createdAt!: Date | null;

  @ApiProperty({
    title: 'Job Phase date requested',
  })
  @Expose()
  jobPhaseDateRequested!: Date;

  @ApiProperty({
    title: 'Job Phase Custom Date Start',
    nullable: true,
  })
  @Expose()
  jobPhaseCustomDateStart!: Date | null;

  @ApiProperty({
    title: 'Job Phase date + time assigned',
  })
  @Expose()
  jobPhaseDateAssigned!: Date;

  @ApiProperty({
    title: 'Job Phase Date Request Due',
    nullable: true,
  })
  @Expose()
  jobPhaseDateRequestDue!: Date | null;

  @ApiProperty({
    title: 'Job Phase Request Delay Days',
    nullable: true,
  })
  @Expose()
  jobPhaseRequestDelayDays!: number;

  @ApiProperty({
    title: 'Job Phase Ready Hour',
    nullable: true,
  })
  @Expose()
  jobPhaseReadyHour!: string | null;

  //
  // Phase Type Fields
  //

  @ApiProperty({
    title: 'Job Phase Type Id',
  })
  @Expose()
  phaseId!: string;

  @ApiProperty({
    title: 'Job Phase Type Name',
  })
  @Expose()
  phaseName!: string;

  @ApiProperty({
    type: 'enum',
    enum: PhaseTypeEnum,
    title: 'Phase Type',
    description: 'The type of this Phase',
  })
  @IsEnum(PhaseTypeEnum)
  @Expose()
  phaseType!: PhaseTypeEnum;

  //
  // Building Fields
  //

  @ApiProperty({
    title: 'Building ID',
    nullable: true,
  })
  @Expose()
  buildingId!: string | null;

  @ApiProperty({
    title: 'Building Number',
    nullable: true,
  })
  @Expose()
  buildingNumber!: string | null;

  //
  // Region fields
  //

  @ApiProperty({
    title: 'Region Id',
  })
  @Expose()
  regionId!: string;

  @ApiProperty({
    title: 'Region Name',
  })
  @Expose()
  regionName!: string;

  //
  // Subdivision fields
  //

  @ApiProperty({
    title: 'Subdivision ID',
  })
  @Expose()
  subdivisionId!: string;

  @ApiProperty({
    title: 'Subdivision Name',
  })
  @Expose()
  subdivisionName!: string;

  @ApiProperty({
    title: 'Supervisor User ID',
  })
  @Expose()
  supervisorUserIds!: string;

  @ApiProperty({
    title: 'Supervisor Full Name',
  })
  @Expose()
  supervisorFullNames!: string;

  //
  // Builder fields
  //

  @ApiProperty({
    title: 'Builder Id',
  })
  @Expose()
  builderId!: string;

  @ApiProperty({
    title: 'Builder Name',
  })
  @Expose()
  builderName!: string;

  //
  // Contractor fields
  //

  @ApiProperty({
    title: 'Contractor Id',
    nullable: true,
  })
  @Expose()
  contractorId!: string | null;

  @ApiProperty({
    title: 'Contractor Name',
    nullable: true,
  })
  @Expose()
  contractorName!: string | null;

  @ApiProperty({
    title: 'Suggested Contractor Id',
    nullable: true,
  })
  @Expose()
  suggestedContractorId!: string | null;

  @ApiProperty({
    title: 'SuggestedContractor Name',
    nullable: true,
  })
  @Expose()
  suggestedContractorName!: string | null;

  //
  // Previous contractor fields
  //

  @ApiProperty({
    title: 'Previous Bump Phase Contractor Id',
    nullable: true,
  })
  @Expose()
  previousBumpContractorId!: string | null;

  @ApiProperty({
    title: 'Previous Bump Phase Contractor Name',
    nullable: true,
  })
  @Expose()
  previousBumpContractorName!: string | null;

  @ApiProperty({
    title: 'Previous Bump Phase Contractor Bumper Available?',
    nullable: true,
  })
  @Expose()
  previousBumpContractorBumperAvailable!: boolean | null;

  @ApiProperty({
    title: 'Previous Bump Phase Contractor Payment Type',
    nullable: true,
  })
  @Expose()
  previousBumpContractorBumperPaymentType!: ContractorPaymentTypeEnum | null;

  //
  // Board receipt fields
  //

  @ApiProperty({
    title: 'Total Payable Boards',
  })
  @Expose()
  payableBoardsTotal!: number | null;

  @ApiProperty({
    title: 'Total Supervisor Count',
  })
  @Expose()
  payableBoardsReceiptTotal!: number | null;

  //
  // Custom fields
  //

  @ApiProperty({
    title: 'Request is on time',
  })
  @Expose()
  requestIsOnTime!: boolean;

  @ApiProperty({
    title: 'Phase Days Count',
  })
  @Expose()
  phaseDaysCount!: number | null;

  @ApiProperty({
    title: 'Job Days Count',
  })
  @Expose()
  jobDaysCount!: number | null;

  @ApiProperty({
    title: 'Late Request',
    description:
      'Indicates if the phase was requested after 3 PM and marked as "late"',
    type: 'boolean',
  })
  @Expose()
  isLateHangRequest!: boolean;

  @ApiProperty({
    title: 'High Ceiling',
    description: 'Indicates if the job has a high ceiling',
    type: 'boolean',
  })
  @Expose()
  isHighCeiling!: boolean;

  @ApiProperty({
    title: 'Default Total Square Footage',
    description: 'Total square footage used by default.',
    default: null,
  })
  @IsOptional()
  @IsInt()
  @Min(1)
  @Expose()
  totalSquareFootage?: number;

  @ApiProperty({
    type: 'enum',
    enum: PhaseStructureTypeEnum,
    nullable: true,
    title: 'Job  Structure Type',
    description: 'Job  Structure Type',
  })
  @IsEnum(PhaseStructureTypeEnum)
  @Expose()
  phaseStructureType!: PhaseStructureTypeEnum;
}
